/**
 * Base Service Class
 * Provides common functionality for all mock data services
 */

// Helper function to create API response
export const createApiResponse = (data, message = 'Success', success = true) => ({
  success,
  data,
  message,
  timestamp: new Date().toISOString()
});

// Helper function to create API error
export const createApiError = (message, code = 'GENERIC_ERROR', details = null) => ({
  success: false,
  error: {
    code,
    message,
    details
  },
  timestamp: new Date().toISOString()
});

export class BaseService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Cache management
  getCached(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache(key = null) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  // Simulate network delay
  async simulateDelay(min = 100, max = 500) {
    const delay = Math.random() * (max - min) + min;
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  // Generic CRUD operations
  async createEntity(entityData, entityType) {
    await this.simulateDelay();
    
    const newEntity = {
      id: `${entityType}_${Date.now()}`,
      ...entityData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    return createApiResponse(newEntity, `${entityType} created successfully`);
  }

  async updateEntity(entityId, entityData, entityType) {
    await this.simulateDelay();
    
    const updatedEntity = {
      ...entityData,
      id: entityId,
      updatedAt: new Date().toISOString()
    };
    
    return createApiResponse(updatedEntity, `${entityType} updated successfully`);
  }

  async deleteEntity(entityId, entityType) {
    await this.simulateDelay();
    
    return createApiResponse({ id: entityId }, `${entityType} deleted successfully`);
  }

  // Generic search functionality
  async searchEntities(query, entities, searchFields = ['name', 'description']) {
    await this.simulateDelay();
    
    const filteredEntities = entities.filter(entity => {
      return searchFields.some(field => {
        const value = entity[field];
        return value && value.toString().toLowerCase().includes(query.toLowerCase());
      });
    });
    
    return createApiResponse(filteredEntities, 'Search completed successfully');
  }

  // Generic filtering functionality
  async filterEntities(entities, filters) {
    await this.simulateDelay();
    
    let filteredEntities = entities;
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        filteredEntities = filteredEntities.filter(entity => {
          const entityValue = entity[key];
          if (Array.isArray(value)) {
            return value.includes(entityValue);
          }
          return entityValue === value;
        });
      }
    });
    
    return createApiResponse(filteredEntities, 'Filtering completed successfully');
  }

  // Generic pagination
  async paginateEntities(entities, page = 1, limit = 10) {
    await this.simulateDelay();
    
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedEntities = entities.slice(startIndex, endIndex);
    
    const paginationInfo = {
      currentPage: page,
      totalPages: Math.ceil(entities.length / limit),
      totalItems: entities.length,
      itemsPerPage: limit,
      hasNextPage: endIndex < entities.length,
      hasPreviousPage: page > 1
    };
    
    return createApiResponse({
      data: paginatedEntities,
      pagination: paginationInfo
    }, 'Pagination completed successfully');
  }
}
